import { Injectable, Inject } from "@angular/core";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from "@angular/common/http";
import { Store } from "@ngrx/store";
import { AppState } from "../models/app.state";
import * as UserActions from "../store/actions/user.actions";
import * as ConfigActions from "../store/actions/config.actions";
import * as HcpActions from "../store/actions/hcp-user.actions";
import * as FhirActions from "../store/actions/fhir.actions";
import * as SupportActionActions from "../store/actions/support-action.actions";
import * as ManualItemActions from "../store/actions/manual-item.actions";
import * as NewsActions from "../store/actions/news.actions";
import * as ServiceMessageActions from "../store/actions/service-message.actions";
import * as QuestionnaireActions from "@zitelab/ionic-fhir-questionnaires"; // TODO: Fix this import, right now it is importing more than just the actions
import * as PatientActions from "../store/actions/patient.actions";
import { JwtHelperService } from "@auth0/angular-jwt";
import { NavController } from "ionic-angular/umd";
import { catchError, switchMap, map } from "rxjs/operators";
import { of } from "rxjs/observable/of";
import { ErrorObservable } from "rxjs/observable/ErrorObservable";
import {
  UserTypes,
  IDiaprofilUser,
  IDiaprofilOrganizationConfig
} from "../models/types";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { AlertController, Events } from "ionic-angular";
import { UserState } from "../store/reducers/user.reducer";
import { extractToken } from "../utils/get-plone-token";
import { brokenUserUuidMapping } from "../utils/broken-user-uuid-mapping";
import { EventLoggingService, LogEventId } from "./event-logging.service";
import { ConfigState } from "../store/reducers/config.reducer";
import { Observable } from "rxjs/Observable";

export interface LogoutResponse {
  error?: {
    message: "Unknown token";
    type: "Logout failed";
  };
}

@Injectable()
export class AuthService {
  baseUrl: URL;
  navCtrl: NavController;
  endpoints: { [key: string]: string };
  jwt: string = null;
  userType: UserTypes;
  userState: UserState;
  jwtHelper: JwtHelperService;
  orgConfig: ConfigState;
  constructor(
    public http: HttpClient,
    public store: Store<AppState>,
    public alertCtrl: AlertController,
    public events: Events,
    private eventLogging: EventLoggingService,
    @Inject(ENV_VARS) public envVars
  ) {
    this.baseUrl = envVars.API_URL;
    this.endpoints = {
      login: "@login",
      logout: "@auth-logout",
      user: "@fhir-user",
      ploneUser: "@users",
      otpResend: this.baseUrl + "/@twofactor-otp-renew/",
      twoFactorVerification: this.baseUrl + "/@twofactor-auth"
    };
    this.store
      .select(state => state.user)
      .subscribe(user => {
        this.jwt = user.jwt;
        this.userType = user.userType;
        this.userState = user;
      });
    this.store
      .select(state => state.config)
      .subscribe(config => {
        this.orgConfig = config;
      });
    this.jwtHelper = new JwtHelperService();
  }

  // See https://angular.io/guide/http#error-handling
  handleLoginError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.log("An error occurred:", error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.log(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    console.log(error);
    let userFacingErrorMessage;
    switch (error.status) {
      case 0:
        userFacingErrorMessage = _i18n_str(
          "AUTH_SERVICE.ERRORS.LOGIN.NO_RESPONSE"
        );
        break;
      case 401:
        userFacingErrorMessage = _i18n_str(
          "AUTH_SERVICE.ERRORS.LOGIN.WRONG_CREDENTIALS"
        );
        break;
      case 303:
        userFacingErrorMessage = _i18n_str(
          "AUTH_SERVICE.ERRORS.LOGIN.2FA_REQUIRED"
        );
        break;
      default:
        userFacingErrorMessage = _i18n_str(
          "AUTH_SERVICE.ERRORS.LOGIN.DEFAULT_MSG"
        );
    }
    // return an ErrorObservable with a user-facing error message
    return new ErrorObservable(userFacingErrorMessage);
  }

  setUserProfile(jwt: string) {
    this.jwt = jwt;
    const jwtProperties = this.jwtHelper.decodeToken(jwt);
    console.log(jwtProperties);
    const userId = jwtProperties.sub;
    this.http
      .get<IDiaprofilUser>(`${this.baseUrl}${this.endpoints.user}/${userId}`)
      .subscribe(result => {
        if (result.user_type !== "Practitioner") {
          this.logout().subscribe(result => {
            const prompt = this.alertCtrl.create({
              title: "Ingen adgang",
              message:
                "Du har ikke de fornødne rettigheder til at tilgå denne løsning",
              buttons: ["OK"]
            });
            prompt.present();
          });
        } else {
          this.store.dispatch(
            new UserActions.LoginAction({
              userName: jwtProperties.fullname,
              uuid: result.username,
              email: result.email,
              organizationUuid: result.organization_uid,
              evalConsent: result.evalConsent,
              evalConsentResponseUuid: result.evalConsentResponseUuid,
              userInitials: result.hcpInitials,
              userFhir: result.user_resource,
              staffType: result.staffType,
              isDeactivated: result.isDeactivated,
              deactivationReason: result.deactivationReason
            })
          );
          this.setOrganizationConfig(result.organization_uid).subscribe();
          this.checkIfSuperUser(result.organization_uid);
          setTimeout(() => {
            this.eventLogging.saveEvent({
              event_id: LogEventId.LOGIN,
              event_src_location: "AuthService"
            });
          }, 2000);
        }
      });
  }

  checkIfSuperUser(organizationUuid: string) {
    const options = {
      params: new HttpParams().set("organization_uuid", organizationUuid)
    };
    this.http
      .get<boolean>(`${this.baseUrl}@is-super-user`, options)
      .subscribe(result => {
        this.store.dispatch(
          new UserActions.UpdateAction({
            isSuperUser: result
          })
        );
      });
  }

  sleep(seconds) {
    var waitUntil = new Date().getTime() + seconds * 1000;
    while (new Date().getTime() < waitUntil) true;
  }

  setOrganizationConfig(
    organizationUuid: string = null
  ): Observable<ConfigState> {
    organizationUuid =
      organizationUuid !== null
        ? organizationUuid
        : this.userState.organizationUuid;

    if (!organizationUuid) {
      this.sleep(1);
      organizationUuid =
        organizationUuid !== null
          ? organizationUuid
          : this.userState.organizationUuid;
    }

    const options = {
      params: new HttpParams().set("include_items", "false")
    };
    return this.http
      .get<IDiaprofilOrganizationConfig>(
        `${this.baseUrl}fhirdatabase/${organizationUuid}`,
        options
      )
      .pipe(
        map(result => {
          console.log({ orgConfig: result });
          const config: ConfigState = {
            ...this.orgConfig,
            orgResource: result.organization_resource,
            patientSupportPersonName:
              result.patient_support_name_of_support_person,
            patientSupportOrgName: result.patient_support_name_of_organization,
            patientSupportPhoneNumber: result.patient_support_telephone_number,
            patientSupportEmail: result.patient_support_email,
            patientSupportAddress: result.patient_support_address,
            hcpSupportPersonName: result.hcp_support_name_of_support_person,
            hcpSupportOrgName: result.hcp_support_name_of_organization,
            hcpSupportPhoneNumber: result.hcp_support_telephone_number,
            hcpSupportEmail: result.hcp_support_email,
            hcpSupportAddress: result.hcp_support_address,
            patientSchemaMedicineTypesRequired:
              result.patient_schema_medicine_types_required,
            patientSchemaMedicineTypesDefault:
              result.patient_schema_medicine_types_default,
            patientSchemaMunicipalityRequired:
              result.patient_schema_municipality_required,
            patientSchemaMunicipalityDefault:
              result.patient_schema_municipality_default,
            patientSchemaDiabetesTypeRequired:
              result.patient_schema_diabetes_type_required,
            patientSchemaDiabetesTypeDefault:
              result.patient_schema_diabetes_type_default,
            patientSchemaDiagnosisYearRequired:
              result.patient_schema_diagnosis_year_required,
            patientSchemaConsentToSendSmsEnabled:
              result.patient_schema_consent_to_send_sms_enabled,
            patientNotificationType: extractToken(result.notification_type),
            humanReadableNotificationPlan:
              result.human_readable_notification_plan,
            evalProject: extractToken(result.eval_project),
            evalProjectParticipationStatus: extractToken(
              result.eval_project_participation_status
            ),
            evalPatientConsentType: extractToken(result.eval_pt_consent_type),
            evalPatientConsentQnr: extractToken(result.eval_pt_consent_qnr),
            evalPatientPostProDemoQnr: extractToken(
              result.eval_pt_post_pro_demo_qnr
            ),
            evalPatientPostVisitQnr: extractToken(
              result.eval_pt_post_visit_qnr
            ),
            evalHcpPostVisitQnr: extractToken(result.eval_hcp_post_visit_qnr),
            evalHcpConsentQnr: extractToken(result.eval_hcp_consent_qnr),
            evalHcpBaselineQnr: extractToken(result.eval_hcp_bl_qnr),
            evalHcpBaselineExtraQnr: extractToken(result.eval_hcp_bl_extra_qnr),
            evalHcpRePatientDemoQnr: extractToken(
              result.eval_hcp_re_pt_demo_qnr
            ),
            evalDataDownload: extractToken(result.eval_data_download_enabled),
            proTaskAvailableDaysBefore: result.pro_task_available_days_before,
            ffProTaskConsultationTypeExtEnabled:
              result.ff_pro_task_consultation_type_extension_enabled,
            ffProTaskConsultationTypeExtDefault: extractToken(
              result.ff_pro_task_consultation_type_extension_default
            ),
            ffProTaskConsultationTypeExtOptions: extractToken(
              result.ff_pro_task_consultation_type_extension_options
            ),
            ffDefaultColumnsInPatientListing: extractToken(
              result.ff_default_columns_in_patient_listing
            ),
            ffDutyOfDisclosurePage: result.ff_duty_of_disclosure_page,
            ffHcpUsersPage: extractToken(result.ff_hcp_users_page),
            ffStatisticsPage: extractToken(result.ff_statistics_page),
            ffDiagnosisTimepointInDashboard: extractToken(
              result.ff_diagnosis_timepoint_in_dashboard
            ),
            ffHcpProPatch: extractToken(result.ff_hcp_pro_patch),
            ffPatientPageDefaultView: extractToken(
              result.ff_patient_page_default_view
            ),
            ffImportFromKihModal: extractToken(result.ff_import_from_kih_modal),
            ffHcp2faLogin: extractToken(result.ff_hcp_2fa_login),
            ffCurrentConsultationsPage: extractToken(
              result.ff_current_consultations_page
            ),
            ffTimeOfConsultationExt: extractToken(
              result.ff_time_of_consultation_ext
            ),
            ffSoftRequiredMobileNumber: extractToken(
              result.ff_soft_required_mobile_number
            ),
            ffSsnRegistryIntegration: extractToken(
              result.ff_ssn_registry_integration
            ),
            ffKramBtnInDashboard: extractToken(result.ff_kram_btn_in_dashboard),
            ffPartialConsultationCompletion: extractToken(
              result.ff_partial_consultation_completion
            ),
            ffNoShowEboksNotificationEnabled:
              result.eboks_task_no_show_letter_template !== null
          };
          this.store.dispatch(new ConfigActions.UpdateAction(config));
          return config;
        })
      );
  }

  login(login: string, password: string, userType: UserTypes) {
    return this.http
      .post<{ token?: string; challenge_url?: string }>(
        `${this.baseUrl}${this.endpoints.login}`,
        {
          login: login,
          password: password
        }
      )
      .pipe(
        catchError(this.handleLoginError),
        switchMap(result => {
          if (result.token) {
            this.store.dispatch(
              new UserActions.SetJWTAction({
                jwt: result.token,
                userType: userType
              })
            );
          }
          return of(result); // 2FA challenge is handled in login page
        })
      );
  }

  initiateTwoFactorSession(challengeUrl: string) {
    return this.http.get(challengeUrl);
  }

  otpResend(token: string, messagingMethod: string) {
    return this.http.post(
      this.endpoints.otpResend,
      {
        twofactor_token: token,
        messaging_method: messagingMethod
      },
      {
        headers: new HttpHeaders()
          .set("Accept", "application/json")
          .set("Content-Type", "application/json")
      }
    );
  }

  twoFactorVerification(otp, token, messagingMethod, userType) {
    return this.http
      .post<{ token: string }>(
        this.endpoints.twoFactorVerification,
        {
          messaging_method: messagingMethod,
          otp: otp,
          twofactor_token: token
        },
        {
          headers: new HttpHeaders()
            .set("Accept", "application/json")
            .set("Content-Type", "application/json")
        }
      )
      .pipe(
        switchMap(result => {
          if (result.token) {
            this.store.dispatch(
              new UserActions.SetJWTAction({
                jwt: result.token,
                userType: userType
              })
            );
          }
          return of(result);
        })
      );
  }

  // See https://angular.io/guide/http#error-handling
  handleLogoutError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.log("An error occurred:", error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.log(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    let userFacingErrorMessage;
    switch (error.status) {
      case 0:
        userFacingErrorMessage = _i18n_str(
          "AUTH_SERVICE.ERRORS.LOGOUT.NO_RESPONSE"
        );
        break;
      case 400:
        this.resetAppState();
        userFacingErrorMessage = _i18n_str(
          "AUTH_SERVICE.ERRORS.LOGOUT.UNKNOWN_TOKEN"
        );
        break;
      case 401:
        if (error.error.type === "HESTIA_ERROR.AUTH.EXPIRED_JWT") {
          userFacingErrorMessage = _i18n_str("HESTIA_ERROR.AUTH.EXPIRED_JWT");
          this.resetAppState(true);
        } else {
          _i18n_str("HESTIA_ERROR.AUTH.UNAUTHORIZED_ACCESS");
        }
        break;
      default:
        userFacingErrorMessage = _i18n_str(
          "AUTH_SERVICE.ERRORS.LOGOUT.DEFAULT_MSG"
        );
    }
    // return an ErrorObservable with a user-facing error message
    return new ErrorObservable(userFacingErrorMessage);
  }

  resetAppState(dueToExpiredToken: boolean = false) {
    this.store.dispatch(new UserActions.LogoutAction());
    this.store.dispatch(new QuestionnaireActions.ResetStateAction());
    this.store.dispatch(new FhirActions.ResetStateAction());
    this.store.dispatch(new PatientActions.ResetStateAction());
    this.store.dispatch(new SupportActionActions.ResetStateAction());
    this.store.dispatch(new ConfigActions.ResetStateAction());
    this.store.dispatch(new ManualItemActions.ResetStateAction());
    this.store.dispatch(new NewsActions.ResetStateAction());
    this.store.dispatch(new HcpActions.ResetStateAction());
    this.store.dispatch(new ServiceMessageActions.ResetStateAction());
    if (dueToExpiredToken) {
      this.events.publish("token:expired");
    }
  }

  changePassword(oldPassword: string, newPassword: string) {
    const userUuidMapping = brokenUserUuidMapping;
    const userUuid =
      userUuidMapping[this.userState.uuid] || this.userState.uuid;
    return this.http.post<{ token: string }>(
      `${this.baseUrl}${this.endpoints.ploneUser}/${userUuid}/reset-password`,
      {
        old_password: oldPassword,
        new_password: newPassword
      }
    );
  }

  logout() {
    this.eventLogging.saveEvent({
      event_id: LogEventId.LOGOUT,
      event_src_location: "AuthService"
    });
    return this.http
      .post<{}>(`${this.baseUrl}${this.endpoints.logout}`, {})
      .pipe(
        catchError(err => {
          return this.handleLogoutError(err);
        }),
        switchMap(
          result => {
            this.resetAppState();
            return of(result);
          },
          error => {
            this.resetAppState();
            return error;
          }
        )
      );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/auth.service.ts